import { JSX } from "react"

import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"

export type InventoryBadgesProps = {
  sellerRank?: number
  eGift?: boolean
  onDemand?: boolean
  hiddenFromStore?: boolean
  hiddenFromInventory?: boolean
  className?: string
}

/**
 * This component renders a set of badges based on the provided props. Each badge represents
 * a specific attribute of an inventory item, such as seller rank, eGift availability, on-demand
 * status, or whether the item is hidden from the store.
 *
 * @param props - The props for the component.
 * @param [props.sellerRank] - The rank of the seller (1-5). If provided and within range, a badge is displayed.
 * @param [props.eGift] - Indicates if the item is available as an eGift. Displays an "eGift" badge if true.
 * @param [props.onDemand] - Indicates if the item is available on demand. Displays an "On Demand" badge if true.
 * @param [props.hiddenFromStore] - Indicates if the item is hidden from the store. Displays a "Hidden from store" badge if true.
 * @param [props.hiddenFromInventory] - Indicates if the item is hidden from inventory. Displays a "Hidden from inventory" badge if true.
 * @param [props.className] - Additional CSS classes to apply to the container.
 *
 * @returns A container with the applicable badges, or hidden if no badges are present.
 */
export function InventoryBadges(props: InventoryBadgesProps): JSX.Element {
  const { className, sellerRank, eGift, onDemand, hiddenFromStore, hiddenFromInventory } = props

  const badges = [
    sellerRank != null && sellerRank <= 5 && sellerRank > 0 && (
      <Bee.Pill key="sellerRank" className="bg-sky-100">
        #{sellerRank} seller in store
      </Bee.Pill>
    ),
    eGift && (
      <Bee.Pill key="eGift" className="bg-orange-100">
        eGift
      </Bee.Pill>
    ),
    onDemand && (
      <Bee.Pill key="onDemand" className="bg-green-100">
        On Demand
      </Bee.Pill>
    ),
    hiddenFromStore && (
      <Bee.Pill key="hiddenFromStore" className="bg-gray-200">
        Hidden from store
      </Bee.Pill>
    ),
    hiddenFromInventory && (
      <Bee.Pill key="hiddenFromInventory" className="bg-gray-200">
        Hidden from inventory
      </Bee.Pill>
    ),
  ].filter((x) => x != null && x !== false)

  return <div className={twMerge("flex flex-wrap gap-2", badges.length <= 0 && "hidden", className)}>{badges}</div>
}
